import { GlobePolarLayer, geometryHasPolarCoordinates } from './globe-polar.js';
import type { Feature, FeatureCollection, Geometry } from 'geojson';
import type { Map as GlobeMap, GeoJSONSource } from 'maplibre-gl';

export class GlobeSource {
  private readonly polar: GlobePolarLayer;
  private readonly images = new Set<string>();
  private readonly featureImages = new Map<string, string>();
  constructor(readonly map: GlobeMap, readonly id: string) {
    this.polar = new GlobePolarLayer(`${id}-polar`, map);
    map.addSource(id, { type: 'geojson', promoteId: 'featureId', data: { type: 'FeatureCollection', features: [] }, tolerance: 0, maxzoom: 22 });
    map.addLayer({ id: `${id}-fill`, source: id, type: 'fill', filter: ['==', ['geometry-type'], 'Polygon'], paint: {
      'fill-color': ['coalesce', ['get', 'fill'], '#3b82f6'], 'fill-opacity': ['coalesce', ['get', 'fillOpacity'], 0.2],
    } }, map.getLayer('3d-buildings') ? '3d-buildings' : undefined);
    map.addLayer({ id: `${id}-line`, source: id, type: 'line', filter: ['==', ['geometry-type'], 'LineString'], layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: {
      'line-color': ['coalesce', ['get', 'color'], '#3b82f6'], 'line-width': ['coalesce', ['get', 'width'], 3], 'line-opacity': ['coalesce', ['get', 'opacity'], 1],
      'line-dasharray': ['case', ['==', ['get', 'dashed'], true], ['literal', [4, 3]], ['literal', [1, 0]]],
    } }, map.getLayer('3d-buildings') ? '3d-buildings' : undefined);
    map.addLayer({ id: `${id}-point`, source: id, type: 'circle', filter: ['all', ['==', ['geometry-type'], 'Point'], ['!', ['has', 'image']]], paint: {
      'circle-radius': ['coalesce', ['get', 'radius'], 6], 'circle-color': ['coalesce', ['get', 'color'], '#3b82f6'], 'circle-opacity': ['coalesce', ['get', 'opacity'], 1], 'circle-stroke-color': ['coalesce', ['get', 'pointOutlineColor'], '#ffffff'], 'circle-stroke-width': 2,
    } });
    map.addLayer({ id: `${id}-image`, source: id, type: 'symbol', filter: ['has', 'image'], layout: {
      'icon-image': ['get', 'image'], 'icon-allow-overlap': true, 'icon-ignore-placement': true, 'icon-pitch-alignment': 'viewport', 'icon-rotation-alignment': 'viewport',
      'icon-offset': ['coalesce', ['get', 'offset'], ['literal', [0, 0]]], 'icon-anchor': ['coalesce', ['get', 'anchor'], 'center'],
    }, paint: { 'icon-opacity': ['coalesce', ['get', 'opacity'], 1] } });
    map.addLayer(this.polar);
  }
  image(id: string, canvas: HTMLCanvasElement, pixelRatio = 1): string {
    const key = `${this.id}:${id}`;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Globe labels require a 2D canvas');
    if (this.map.hasImage(key)) this.map.removeImage(key);
    this.map.addImage(key, context.getImageData(0, 0, canvas.width, canvas.height), { pixelRatio });
    this.images.add(key);
    this.polar.image(key, canvas, pixelRatio);
    return key;
  }
  removeImage(id: string): void {
    const key = `${this.id}:${id}`;
    if (this.map.hasImage(key)) this.map.removeImage(key);
    this.images.delete(key);
    this.polar.removeImage(key);
  }
  private retainImages(remove: string[], add: Feature[]): void {
    remove.forEach(id => this.featureImages.delete(id));
    for (const feature of add) {
      if (feature.properties?.image) this.featureImages.set(String(feature.id), feature.properties.image);
    }
    const retained = new Set(this.featureImages.values());
    for (const key of this.images) if (!retained.has(key)) {
      if (this.map.hasImage(key)) this.map.removeImage(key);
      this.images.delete(key); this.polar.removeImage(key);
    }
  }
  set(features: Feature[]): void {
    this.featureImages.clear();
    this.retainImages([], features);
    const source = this.map.getSource<GeoJSONSource>(this.id);
    this.polar.clear();
    this.polar.patch([], features.filter(feature => geometryHasPolarCoordinates(feature.geometry)));
    if (source) void source.setData({ type: 'FeatureCollection', features: features.filter(feature => !geometryHasPolarCoordinates(feature.geometry)) }).catch(error => this.map.fire('error', { error }));
  }
  patch(remove: string[], add: Feature[]): void {
    if (!remove.length && !add.length) return;
    this.retainImages(remove, add);
    const source = this.map.getSource<GeoJSONSource>(this.id);
    this.polar.patch(remove, add.filter(feature => geometryHasPolarCoordinates(feature.geometry)));
    if (source) void source.updateData({ remove, add: add.filter(feature => !geometryHasPolarCoordinates(feature.geometry)) }).catch(error => this.map.fire('error', { error }));
  }
  visible(value: boolean): void {
    this.polar.setVisible(value);
    for (const suffix of ['fill', 'line', 'point', 'image']) this.map.setLayoutProperty(`${this.id}-${suffix}`, 'visibility', value ? 'visible' : 'none');
  }
  destroy(): void {
    if (this.map.getLayer(`${this.id}-polar`)) this.map.removeLayer(`${this.id}-polar`);
    for (const suffix of ['fill', 'line', 'point', 'image']) if (this.map.getLayer(`${this.id}-${suffix}`)) this.map.removeLayer(`${this.id}-${suffix}`);
    if (this.map.getSource(this.id)) this.map.removeSource(this.id);
    for (const key of this.images) if (this.map.hasImage(key)) this.map.removeImage(key);
    this.images.clear(); this.featureImages.clear();
  }
}

export function globeFeature(id: string, geometry: Feature['geometry'], properties: Feature['properties'] = {}): Feature {
  return { type: 'Feature', id, geometry, properties: { ...properties, featureId: id } };
}

export function globeBounds(features: FeatureCollection['features']): [[number, number], [number, number]] | null {
  let west = Infinity, south = Infinity, east = -Infinity, north = -Infinity;
  function position(point: number[]): void {
    west = Math.min(west, point[0]); south = Math.min(south, point[1]);
    east = Math.max(east, point[0]); north = Math.max(north, point[1]);
  }
  function visit(geometry: Geometry): void {
    switch (geometry.type) {
      case 'Point': position(geometry.coordinates); break;
      case 'MultiPoint': case 'LineString': geometry.coordinates.forEach(position); break;
      case 'MultiLineString': case 'Polygon': for (const line of geometry.coordinates) line.forEach(position); break;
      case 'MultiPolygon': for (const polygon of geometry.coordinates) for (const ring of polygon) ring.forEach(position); break;
      case 'GeometryCollection': geometry.geometries.forEach(visit); break;
    }
  }
  for (const feature of features) visit(feature.geometry);
  return west === Infinity ? null : [[west, south], [east, north]];
}
